import { supabase } from '../firebase/config';

export type SupportTicketType = 'customer_support' | 'vendor_support' | 'product_flag';
export type SupportTicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type SupportTicketPriority = 'low' | 'normal' | 'high';

export interface SupportTicket {
  id: string;
  ticketNumber: string;
  type: SupportTicketType;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  subject: string;
  message: string;
  reporterId: string;
  reporterEmail: string;
  reporterName: string;
  reporterType: 'customer' | 'vendor' | 'admin';
  productId?: string;
  productName?: string;
  adminNotes?: string;
  createdAt: Date;
  updatedAt: Date;
  resolvedAt?: Date;
}

export interface CreateSupportTicketInput {
  type: SupportTicketType;
  subject: string;
  message: string;
  reporterId: string;
  reporterEmail: string;
  reporterName: string;
  reporterType: 'customer' | 'vendor' | 'admin';
  productId?: string;
  productName?: string;
  priority?: SupportTicketPriority;
}

const mapTicket = (row: Record<string, unknown>): SupportTicket => ({
  id: row.id as string,
  ticketNumber: row.ticket_number as string,
  type: row.type as SupportTicketType,
  status: row.status as SupportTicketStatus,
  priority: row.priority as SupportTicketPriority,
  subject: row.subject as string,
  message: row.message as string,
  reporterId: row.reporter_id as string,
  reporterEmail: row.reporter_email as string,
  reporterName: row.reporter_name as string,
  reporterType: row.reporter_type as SupportTicket['reporterType'],
  productId: (row.product_id as string) || undefined,
  productName: (row.product_name as string) || undefined,
  adminNotes: (row.admin_notes as string) || undefined,
  createdAt: new Date(row.created_at as string),
  updatedAt: new Date(row.updated_at as string),
  resolvedAt: row.resolved_at ? new Date(row.resolved_at as string) : undefined,
});

const generateTicketNumber = () => {
  const stamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `WA-${stamp}-${random}`;
};

class SupportTicketService {
  async createTicket(input: CreateSupportTicketInput): Promise<string> {
    const now = new Date().toISOString();
    const payload = {
      ticket_number: generateTicketNumber(),
      type: input.type,
      status: 'open',
      priority: input.priority || 'normal',
      subject: input.subject.trim(),
      message: input.message.trim(),
      reporter_id: input.reporterId,
      reporter_email: input.reporterEmail,
      reporter_name: input.reporterName,
      reporter_type: input.reporterType,
      product_id: input.productId || null,
      product_name: input.productName || null,
      created_at: now,
      updated_at: now,
    };

    const { data, error } = await supabase
      .from('support_tickets')
      .insert(payload)
      .select('id')
      .single();

    if (error) throw error;
    return data.id;
  }

  async getMyTickets(userId: string): Promise<SupportTicket[]> {
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('reporter_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapTicket);
  }

  async getAllTickets(): Promise<SupportTicket[]> {
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapTicket);
  }

  async updateTicket(
    ticketId: string,
    updates: {
      status?: SupportTicketStatus;
      priority?: SupportTicketPriority;
      adminNotes?: string;
    }
  ): Promise<void> {
    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };

    if (updates.status !== undefined) {
      updateData.status = updates.status;
      if (updates.status === 'resolved' || updates.status === 'closed') {
        updateData.resolved_at = new Date().toISOString();
      }
    }
    if (updates.priority !== undefined) updateData.priority = updates.priority;
    if (updates.adminNotes !== undefined) updateData.admin_notes = updates.adminNotes;

    const { error } = await supabase
      .from('support_tickets')
      .update(updateData)
      .eq('id', ticketId);

    if (error) throw error;
  }

  getTicketTypeLabel(type: SupportTicketType): string {
    switch (type) {
      case 'customer_support':
        return 'Customer Support';
      case 'vendor_support':
        return 'Vendor Support';
      case 'product_flag':
        return 'Product Flag';
      default:
        return type;
    }
  }
}

export const supportTicketService = new SupportTicketService();
